/**
 * SDMS Qualifier-column dropdown injection — the in-cell combobox the OG ALPACA
 * (G5) SDMS sheet shows on the QUALIFIER column so the analyst can pick a symbol
 * right in the grid.
 *
 * WHY THIS EXISTS. OG ALPACA adds that dropdown at RUNTIME via
 * `SpreadsheetEntryController.DataValidations.Add(...)` — it never reaches the saved
 * template, so `collectDataValidations` (sdmsDataValidation.ts, which ports only the
 * validations authored INTO the workbook) could not render it. G6 had only the ribbon
 * "Qualifiers" multi-select picker (SdmsQualifierPicker), not the in-cell column combo.
 * This closes that gap.
 *
 * TEMPLATE-AGNOSTIC. The QUALIFIER column + its first data-band row come from the
 * merge's `fieldRefs["QUALIFIER"]` ("Sheet!A1" — the same anchor the QCBATCHID stamp
 * uses in SdmsResultEntryPage), so no per-template column lookup is needed. The list is
 * the controlled dbo.Qualifiers symbols (`/api/v1/analysis/qualifiers`), written to a
 * hidden helper sheet and referenced as a range — 55 symbols exceed Excel's ~255-char
 * inline-list cap, exactly why the dept-chain dropdown uses a helper sheet too.
 *
 * It runs as a small post-merge step (a sibling of `applyDeptChainDropdowns`), reusing
 * the existing `collectDataValidations` → Univer in-cell dropdown path, and is a strict
 * NO-OP when the template has no QUALIFIER field or the qualifiers list can't be loaded.
 * A DevExpress list data-validation is SINGLE-select ("pick a value"); the ribbon picker
 * remains for multi-symbol entry.
 */
import { api } from '../../api/client';
import { isResultSheet } from './sdmsEngineOverlay';
import { parseCellRef } from './sdmsDeptChainDropdown';
import { loadSdmsWorkbook, writeSdmsWorkbook } from './sdmsWorkbookIo';

// OG's SetQualifierDropdown names the list sheet exactly "Qualifier" and HIDES it (Visible = false — Hidden, not
// VeryHidden; SpreadsheetEntryController :31768-31777). AI used "QualifierList" + veryHidden, so OG opened a sheet it
// did not know (OG vs AI comparison 2026-09-28, point 12). Our view loads only visible sheets either way.
const QUALIFIER_LIST_SHEET = 'Qualifier';

// The Qualifiers lookup is a small, static reference list (~55 rows), so cache it for
// the session. Only a real (non-empty) list is cached — a transient failure retries on
// the next batch open rather than sticking an empty dropdown.
let symbolCache: string[] | null = null;

/** OG orders qualifiers by SYMBOL ASCENDING in both SDMS surfaces — the sheet dropdown
 *  (SetQualifierDropdown :31420 `OrderBy(q => q.Symbol)`) and the Qualifiers popup
 *  (`Qualifiers_ListView_SDMS`: Symbol SortIndex=0 Ascending). The shared
 *  `/api/v1/analysis/qualifiers` feed is QualifierID-ordered (its other consumer, the Data
 *  Package Check Points picker, has NO sort in OG), so the two SDMS consumers sort here.
 *  Case-insensitive, culture-aware compare ≈ the SQL CI collation / .NET string OrderBy OG
 *  runs through. ONE comparator for both so they can never drift. (2026-09-22) */
export function compareQualifierSymbols(a: string, b: string): number {
  return a.localeCompare(b, 'en', { sensitivity: 'base' }) || a.localeCompare(b, 'en');
}

/** Distinct, non-blank qualifier Symbols from `/api/v1/analysis/qualifiers` (the same
 *  feed SdmsQualifierPicker uses), in OG's Symbol-ascending order. Returns [] on any
 *  failure — the caller then no-ops. */
export async function fetchQualifierSymbols(): Promise<string[]> {
  if (symbolCache) return symbolCache;
  try {
    const res = await api.get('/api/v1/analysis/qualifiers', { silentLoading: true } as never);
    const rows = (res.data as { data?: { symbol?: string | null }[] }).data ?? [];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const r of rows) {
      const s = (r.symbol ?? '').trim();
      if (!s || seen.has(s)) continue;
      seen.add(s);
      out.push(s);
    }
    out.sort(compareQualifierSymbols);
    if (out.length) symbolCache = out;
    return out;
  } catch {
    return [];
  }
}

/** True for a sheet that carries the sample result band (the main sheet + Model=1/2
 *  per-run / per-level copies) — NOT a sub/config sheet and NOT our own helper. The
 *  QUALIFIER column sits at the same position on each, so the dropdown belongs there. */
function isResultSheetAt(name: string, index: number): boolean {
  const u = name.toUpperCase();
  return u !== QUALIFIER_LIST_SHEET.toUpperCase() && isResultSheet(name, index);
}

/**
 * Inject the Qualifier-column list dropdown into the merged workbook `buf`. Returns the
 * bytes UNCHANGED when there is nothing to inject (no QUALIFIER field in the template,
 * an unparseable anchor, or an empty symbols list).
 *
 * @param qualifierRef  fieldRefs["QUALIFIER"] — "Sheet!A1" of the qualifier column's
 *                       first data-band cell for this merge.
 * @param symbols       controlled qualifier Symbols (from `fetchQualifierSymbols`).
 */
export async function applyQualifierDropdown(
  buf: ArrayBuffer,
  qualifierRef: string | undefined | null,
  symbols: readonly string[],
): Promise<ArrayBuffer> {
  if (!qualifierAnchor(qualifierRef, symbols)) return buf;
  const wb = await loadSdmsWorkbook(buf);
  // ExcelJS drops `text=` from containsText conditional-formatting rules on write, which
  // makes DevExpress (ALPACA) refuse the workbook; writeSdmsWorkbook restores it (no-op when
  // none). Same guard the dept-chain injection uses. See sdmsCfTextRepair.ts.
  return applyQualifierDropdownToWorkbook(wb, qualifierRef, symbols) ? writeSdmsWorkbook(wb) : buf;
}

function qualifierAnchor(qualifierRef: string | undefined | null, symbols: readonly string[]): { col: number; row: number } | null {
  if (!qualifierRef || symbols.length === 0) return null;
  const bang = qualifierRef.lastIndexOf('!');
  if (bang < 0) return null;
  return parseCellRef(qualifierRef.slice(bang + 1));   // { col, row } 1-based
}

/** The same injection on a workbook already in memory (the open chain — sdmsWorkbookIo.ts). Returns
 *  false, leaving `wb` untouched, when there is nothing to inject (no anchor / symbols / result sheet). */
export function applyQualifierDropdownToWorkbook(
  wb: import('exceljs').Workbook,
  qualifierRef: string | undefined | null,
  symbols: readonly string[],
): boolean {
  const anchor = qualifierAnchor(qualifierRef, symbols);
  if (!anchor) return false;
  // Checked before the helper sheet is added: the buffer form discarded the whole edit (returned the
  // untouched bytes) when no result sheet took the dropdown. The helper sheet is never a result sheet
  // and is appended last, so this is the same test.
  if (!wb.worksheets.some((ws, i) => isResultSheetAt(ws.name, i))) return false;

  // Hidden helper sheet holding the symbols; the DV references its range (a long symbol
  // list exceeds Excel's inline-list cap). Hidden, as OG.
  let listWs = wb.getWorksheet(QUALIFIER_LIST_SHEET);
  if (!listWs) listWs = wb.addWorksheet(QUALIFIER_LIST_SHEET);
  symbols.forEach((sym, i) => { listWs!.getCell(i + 1, 1).value = sym; });
  (listWs as unknown as { state: string }).state = 'hidden';
  const listRef = `${QUALIFIER_LIST_SHEET}!$A$1:$A$${symbols.length}`;

  for (const ws of wb.worksheets) {
    if (!isResultSheetAt(ws.name, wb.worksheets.indexOf(ws))) continue;
    // The merge trims empty rows below the band, so the last populated row ≈ the last
    // data row. Fill the dropdown DOWN the whole data band (like a Continuous field).
    let lastRow = 1;
    ws.eachRow({ includeEmpty: false }, (_row, n) => { if (n > lastRow) lastRow = n; });
    const endRow = Math.max(anchor.row, lastRow);
    for (let r = anchor.row; r <= endRow; r++) {
      ws.getCell(r, anchor.col).dataValidation = { type: 'list', allowBlank: true, formulae: [listRef] };
    }
  }
  return true;
}
